import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import {
  getActiveViewCamera,
  getActiveViewId,
  getOptionalViewRegistry,
  getViewToolsRoot,
  isActiveViewFrame,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import { ClipTask, EPSILON } from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'
import type {
  ClipApplyMode,
  ClipBoxEditorState,
  ClipDraftState,
  ClipSelectionMode,
  ClipVolume,
  Disposable,
  InputConsumer,
  NormalizedKeyEvent,
  NormalizedPointerEvent,
} from '@/shared/types/core'
import type { ActivatableToolPlugin, ActivationToken } from '@/shared/types/plugin'
import type {
  CameraRig,
  ClipService,
  ControlManager,
  InputRouter,
  PointCloudService,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { BoxTransformGizmo } from './BoxTransformGizmo'

const log = getLoggerManager().getLogger('tools')

const PREVIEW_GROUP_ID = 'interactive-preview'
const LIVE_BOX_GROUP_ID = 'interactive-box3d-live'
const CONFIRMED_GROUP_PREFIX = 'interactive-confirmed'
const CLICK_MOVE_THRESHOLD = 5
const MIN_BOX_EDGE = 1
const FOCUS_VIEW_PADDING = 1.2
const FOCUS_POLE_OFFSET_RATIO = 1e-3
const FOCUS_WORLD_UP = new THREE.Vector3(0, 0, 1)
type BoxSeedSource = 'pointcloud' | 'scene' | 'default'

/** 剪裁选区模式别名，保留向后兼容 */
export type ClipMode = ClipSelectionMode

function toClipPoint(ndc: THREE.Vector2): THREE.Vector3 {
  return new THREE.Vector3(ndc.x, ndc.y, 0)
}

function clonePoints(points: ReadonlyArray<THREE.Vector3>): THREE.Vector3[] {
  return points.map((point) => point.clone())
}

function buildRectanglePoints(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
  return [
    new THREE.Vector3(start.x, start.y, 0),
    new THREE.Vector3(end.x, start.y, 0),
    new THREE.Vector3(end.x, end.y, 0),
    new THREE.Vector3(start.x, end.y, 0),
  ]
}

function computeClipAabb(points: ReadonlyArray<THREE.Vector3>) {
  const min = new THREE.Vector2(Infinity, Infinity)
  const max = new THREE.Vector2(-Infinity, -Infinity)

  for (const point of points) {
    min.x = Math.min(min.x, point.x)
    min.y = Math.min(min.y, point.y)
    max.x = Math.max(max.x, point.x)
    max.y = Math.max(max.y, point.y)
  }

  return { min, max }
}

function composeBoxMatrix(box: THREE.Box3, minBoxEdge: number): THREE.Matrix4 {
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())

  size.x = Math.max(size.x, minBoxEdge)
  size.y = Math.max(size.y, minBoxEdge)
  size.z = Math.max(size.z, minBoxEdge)

  return new THREE.Matrix4().compose(center, new THREE.Quaternion(), size)
}

function clipModeToTask(mode: ClipApplyMode): ClipTask {
  return mode === 'inside' ? ClipTask.SHOW_INSIDE : ClipTask.SHOW_OUTSIDE
}

function rangeToBox3(range: unknown): THREE.Box3 | null {
  if (!Array.isArray(range) || range.length !== 2) {
    return null
  }

  const [min, max] = range
  if (!Array.isArray(min) || min.length !== 3 || !Array.isArray(max) || max.length !== 3) {
    return null
  }

  const values = [...min, ...max]
  if (values.some((value) => typeof value !== 'number' || !Number.isFinite(value))) {
    return null
  }

  return new THREE.Box3(
    new THREE.Vector3(min[0], min[1], min[2]),
    new THREE.Vector3(max[0], max[1], max[2])
  )
}

/**
 * 剪裁工具插件，实现多边形/矩形/3D 盒选区的创建、编辑与裁剪应用。
 */
export class ClipPlugin implements ActivatableToolPlugin, ClipService {
  /** 插件名称 */
  readonly name = 'clip'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 50
  /** 所属互斥组 */
  readonly group = 'default'
  /** 依赖的服务名列表 */
  readonly dependencies = ['InputRouter', 'CameraRig'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['ClipService'] as const

  private _active = false
  private activationToken: ActivationToken | null = null
  private context!: EngineContext
  private inputRouter!: InputRouter
  private cameraRig!: CameraRig
  private controlManager: ControlManager | null = null
  private currentCamera: THREE.Camera | null = null
  private viewRegistry: ViewRegistry | null = null

  private _volumes: ClipVolume[] = []
  private listeners = new Set<(volumes: ReadonlyArray<ClipVolume>) => void>()
  private previewSelectionVolumes: ClipVolume[] = []
  private confirmedGroupCount = 0
  private lockedController: THREE.Controls<any> | null = null
  private lockedControllerWasEnabled = false

  private _selectionMode: ClipSelectionMode = 'polygon'
  private _clipApplyMode: ClipApplyMode = 'inside'
  private _draftPoints: THREE.Vector3[] = []
  private _draftPreviewPoint: THREE.Vector3 | null = null

  private pendingPointer: {
    button: number
    startScreen: THREE.Vector2
    moved: boolean
  } | null = null

  private readonly draftBoxAnchor = new THREE.Object3D()
  private draftBoxMatrix: THREE.Matrix4 | null = null
  private draftBoxSeedSource: BoxSeedSource | null = null
  private draftBoxWasUserModified = false
  private boxGizmo: BoxTransformGizmo | null = null
  private minBoxEdge = MIN_BOX_EDGE
  private readonly onContextMenu = (event: MouseEvent) => {
    if (!this._active || this._selectionMode === 'box3d') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
  }

  private readonly handleViewActivated = (event: {
    viewId: string
    previousViewId: string | null
  }) => {
    this.currentCamera = this.resolveActiveViewCamera()
    this.reattachDraftBoxAnchor(event.viewId)
    if (this.currentCamera) {
      this.boxGizmo?.setCamera(this.currentCamera)
    }

    if (
      event.previousViewId &&
      event.previousViewId !== event.viewId &&
      this._selectionMode !== 'box3d'
    ) {
      const hadDraft =
        this._draftPoints.length > 0 ||
        this._draftPreviewPoint !== null ||
        this.previewSelectionVolumes.length > 0
      this.pendingPointer = null
      if (hadDraft) {
        this._draftPoints = []
        this._draftPreviewPoint = null
        this.previewSelectionVolumes = []
        this.emitChanged()
        return
      }
    }

    this.syncControlLock()
  }

  private readonly consumer: InputConsumer = {
    name: 'clip',
    priority: 100,

    onPointerDown: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerDown(event)
    },

    onPointerMove: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerMove(event)
    },

    onPointerUp: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerUp(event)
    },

    onWinKeyDown: (event: NormalizedKeyEvent): boolean => {
      return this.handleKeyDown(event)
    },
  }

  /** 是否处于激活状态 */
  get isActive(): boolean {
    return this._active
  }

  /** 当前生效的剪裁体列表，含已确认与预览 */
  get volumes(): ReadonlyArray<ClipVolume> {
    if (this.previewSelectionVolumes.length === 0) {
      return this._volumes
    }

    return [...this._volumes, ...this.previewSelectionVolumes]
  }

  /**
   * 获取当前选区模式。
   *
   * @returns 当前选区模式（polygon/rectangle/box3d）
   */
  getSelectionMode(): ClipSelectionMode {
    return this._selectionMode
  }

  /**
   * 设置选区模式，切换时清理草稿。
   *
   * @param mode - 目标选区模式
   */
  setSelectionMode(mode: ClipSelectionMode): void {
    if (this._selectionMode === mode) return

    const previousMode = this._selectionMode
    this._selectionMode = mode
    const preservePreview =
      (previousMode === 'polygon' || previousMode === 'rectangle') &&
      (mode === 'polygon' || mode === 'rectangle')

    this.pendingPointer = null
    this._draftPoints = []
    this._draftPreviewPoint = null
    if (!preservePreview) {
      this.previewSelectionVolumes = []
    }
    if (mode === 'box3d') {
      this._clipApplyMode = 'inside'
      if (!this.restoreDraftBoxFromLiveVolume()) {
        this.resetBoxDraft()
      }
    }

    if (this._active && mode === 'box3d') {
      this.ensureBoxDraftInitialized()
      this.ensureBoxGizmo()
      this.syncLiveBoxVolume()
    } else if (mode !== 'box3d') {
      this.disposeBoxGizmo()
    }

    this.syncControlLock()
    this.prewarmGaussianSplatClip()
    this.emitChanged()
  }

  /**
   * 设置选区模式的兼容别名。
   *
   * @param mode - 目标选区模式
   */
  setClipMode(mode: ClipSelectionMode): void {
    this.setSelectionMode(mode)
  }

  /**
   * 获取选区模式的兼容别名。
   *
   * @returns 当前选区模式
   */
  getClipMode(): ClipSelectionMode {
    return this.getSelectionMode()
  }

  /**
   * 获取裁剪应用模式。
   *
   * @returns 当前裁剪应用模式（inside/outside）
   */
  getClipApplyMode(): ClipApplyMode {
    return this._clipApplyMode
  }

  /**
   * 设置裁剪应用模式，box3d 模式下忽略。
   *
   * @param mode - 目标应用模式
   */
  setClipApplyMode(mode: ClipApplyMode): void {
    if (this._selectionMode === 'box3d') {
      return
    }
    if (this._clipApplyMode === mode) return
    this._clipApplyMode = mode
    this.emitChanged()
  }

  /**
   * 获取当前草稿状态快照。
   *
   * @returns 草稿状态，含顶点、预览点、box 矩阵、是否可确认
   */
  getDraftState(): ClipDraftState {
    if (this._selectionMode === 'box3d') {
      this.syncDraftBoxMatrix()
    }

    return {
      selectionMode: this._selectionMode,
      clipMode: this._clipApplyMode,
      points: clonePoints(this._draftPoints),
      previewPoint: this._draftPreviewPoint?.clone() ?? null,
      boxMatrix: this.draftBoxMatrix?.clone() ?? null,
      canConfirm: this.canConfirmDraft(),
    }
  }

  /** 当前草稿顶点数组 */
  get currentVertices(): ReadonlyArray<THREE.Vector3> {
    return this._draftPoints
  }

  /**
   * 获取 box3d 手柄根节点，用于外部渲染挂接。
   *
   * @returns 手柄根节点；未创建返回 null
   */
  getBoxGizmoHelper(): THREE.Object3D | null {
    return this.boxGizmo?.root ?? null
  }

  /**
   * 记录外部分配的激活令牌。
   *
   * @param token - 激活令牌
   */
  setActivationToken(token: ActivationToken): void {
    this.activationToken = token
  }

  /**
   * 初始化插件，注册 ClipService。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    context.registerService('ClipService', this)
  }

  /** 启动插件，解析依赖并订阅视口事件 */
  onStart(): void {
    this.inputRouter = this.context.getService('InputRouter')
    this.cameraRig = this.context.getService('CameraRig')
    this.context.container.addEventListener('contextmenu', this.onContextMenu)
    this.draftBoxAnchor.name = 'clipDraftBoxAnchor'
    this.draftBoxAnchor.visible = false
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.currentCamera = this.resolveActiveViewCamera()
    this.reattachDraftBoxAnchor(getActiveViewId(this.viewRegistry))
    if (this.context.hasService('ControlManager')) {
      this.controlManager = this.context.getService('ControlManager')
    }
    if (this.viewRegistry) {
      this.context.events.on('view.activated', this.handleViewActivated)
    }
  }

  /**
   * 渲染视图前更新相机、手柄视口与草稿同步。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!isActiveViewFrame(this.viewRegistry, frame)) return

    this.currentCamera = this.resolveActiveViewCamera() ?? frame.camera
    this.reattachDraftBoxAnchor(this.resolveActiveViewId())
    this.syncControlLock()

    if (!this._active) return

    if (this._selectionMode === 'box3d') {
      this.ensureBoxDraftInitialized()
      this.ensureBoxGizmo()
      if (this.boxGizmo) {
        this.boxGizmo.setCamera(this.currentCamera)
        this.boxGizmo.setViewport(frame.viewport.width, frame.viewport.height)
        this.boxGizmo.update()
      }
      this.syncDraftBoxMatrix()
    }
  }

  /** 销毁插件，取消订阅、释放手柄、清空状态 */
  onDestroy(): void {
    if (this.viewRegistry) {
      this.context.events.off('view.activated', this.handleViewActivated)
    }
    if (this._active && this.activationToken) {
      this.deactivate(this.activationToken)
    }
    this.previewSelectionVolumes = []
    this.releaseControlLock()
    this.context.container.removeEventListener('contextmenu', this.onContextMenu)
    this.disposeBoxGizmo()
    this.draftBoxAnchor.removeFromParent()
    this.listeners.clear()
    this._volumes = []
    this.viewRegistry = null
  }

  /**
   * 激活工具，注册输入消费者并初始化草稿。
   *
   * @param token - 激活令牌，用于与其它工具互斥校验
   */
  activate(token: ActivationToken): void {
    this.validateToken(token)
    if (this._active) return

    this._active = true
    this.currentCamera = this.resolveActiveViewCamera() ?? this.currentCamera
    this.reattachDraftBoxAnchor(this.resolveActiveViewId())
    this.inputRouter.addConsumer(this.consumer)

    if (this._selectionMode === 'box3d') {
      this.ensureBoxDraftInitialized()
      this.ensureBoxGizmo()
      this.syncLiveBoxVolume()
    }

    this.syncControlLock()
    this.prewarmGaussianSplatClip()
    this.emitChanged()
  }

  /**
   * 停用工具，移除输入消费者并清理草稿。
   *
   * @param token - 激活令牌
   */
  deactivate(token: ActivationToken): void {
    this.validateToken(token)
    if (!this._active) return

    this._active = false
    this.inputRouter.removeConsumer(this.consumer)
    this.pendingPointer = null
    this.currentCamera = null
    this._draftPoints = []
    this._draftPreviewPoint = null
    this.previewSelectionVolumes = []
    this.disposeBoxGizmo()
    this.syncControlLock()
    this.releaseGaussianSplatClipPrewarm()
    this.emitChanged()
  }

  /**
   * 订阅剪裁体变更。
   *
   * @param callback - 变更回调，接收最新剪裁体列表
   * @returns 订阅句柄，调用 dispose 取消订阅
   */
  onChanged(callback: (volumes: ReadonlyArray<ClipVolume>) => void): Disposable {
    this.listeners.add(callback)
    return {
      dispose: () => {
        this.listeners.delete(callback)
      },
    }
  }

  /**
   * 获取指定分组的剪裁体。
   *
   * @param groupId - 分组 ID
   * @returns 匹配的剪裁体数组；无匹配返回空数组
   */
  getVolumesByGroup(groupId: string): ReadonlyArray<ClipVolume> {
    return this.volumes.filter((volume) => volume.groupId === groupId)
  }

  /**
   * 替换分组下的剪裁体集合。
   *
   * @param groupId - 分组 ID；传入 `interactive-preview` 表示预览组
   * @param volumes - 新的剪裁体列表，将覆盖该组旧数据
   */
  setGroupClipTask(groupId: string, volumes: ClipVolume[]): void {
    if (groupId === PREVIEW_GROUP_ID) {
      this.previewSelectionVolumes = []
      for (const volume of volumes) {
        volume.groupId = groupId
        this.normalizeVolume(volume)
        this.previewSelectionVolumes.push(volume)
      }
      this.emitChanged()
      return
    }

    this._volumes = this._volumes.filter((volume) => volume.groupId !== groupId)

    for (const volume of volumes) {
      volume.groupId = groupId
      this.normalizeVolume(volume)
      this._volumes.push(volume)
    }

    this.emitChanged()
  }

  /**
   * 添加剪裁体。
   *
   * @param volume - 剪裁体；若 groupId 为预览组则加入预览集合
   */
  addVolume(volume: ClipVolume, options?: { viewId?: string | null }): void {
    volume.viewId = options?.viewId ?? volume.viewId ?? undefined

    if (volume.groupId === PREVIEW_GROUP_ID) {
      this.normalizeVolume(volume)
      this.previewSelectionVolumes.push(volume)
      this.emitChanged()
      return
    }

    this.normalizeVolume(volume)
    this._volumes.push(volume)
    this.emitChanged()
  }

  /**
   * 移除剪裁体。
   *
   * @param volume - 待移除的剪裁体；不存在时忽略
   */
  removeVolume(volume: ClipVolume): void {
    const previewIndex = this.previewSelectionVolumes.indexOf(volume)
    if (previewIndex !== -1) {
      this.previewSelectionVolumes.splice(previewIndex, 1)
      this.emitChanged()
      return
    }

    const index = this._volumes.indexOf(volume)
    if (index === -1) return

    this._volumes.splice(index, 1)
    this.emitChanged()
  }

  /**
   * 获取 box3d 编辑器状态。
   *
   * @returns 编辑器状态，含激活标志、拖拽中、矩阵与位姿
   */
  getBoxEditorState(): ClipBoxEditorState {
    const active = this._active && this._selectionMode === 'box3d'
    const hasBox = active && this.draftBoxMatrix !== null
    return {
      active,
      dragging: this.boxGizmo?.isDragging ?? false,
      selectionMode: this._selectionMode,
      boxMatrix: hasBox ? this.draftBoxMatrix!.clone() : null,
      boxPosition: hasBox ? this.draftBoxAnchor.position.clone() : null,
      boxScale: hasBox ? this.draftBoxAnchor.scale.clone() : null,
      boxQuaternion: hasBox ? this.draftBoxAnchor.quaternion.clone() : null,
    }
  }

  /** 设置 3D 裁切盒每轴的最小边长；必须是有限正数。 */
  setBoxMinEdge(minBoxEdge: number): void {
    if (!Number.isFinite(minBoxEdge) || minBoxEdge <= 0) {
      throw new RangeError('minBoxEdge must be a finite positive number')
    }
    this.minBoxEdge = minBoxEdge
    this.boxGizmo?.setMinBoxEdge(minBoxEdge)
    if (this._active && this._selectionMode === 'box3d') {
      this.syncDraftBoxMatrix()
      this.emitChanged()
    }
  }

  /**
   * 程序化设置 box3d 编辑器的变换。
   *
   * @param transform - 平移/旋转/缩放子集，仅应用提供的字段
   * @returns 成功应用返回 true；非 box3d 模式或未激活返回 false
   */
  setBoxEditorTransform(transform: {
    position?: THREE.Vector3
    quaternion?: THREE.Quaternion
    scale?: THREE.Vector3
  }): boolean {
    if (!this._active || this._selectionMode !== 'box3d') {
      return false
    }
    if (transform.position) {
      this.draftBoxAnchor.position.copy(transform.position)
    }
    if (transform.quaternion) {
      this.draftBoxAnchor.quaternion.copy(transform.quaternion)
    }
    if (transform.scale) {
      this.draftBoxAnchor.scale.copy(transform.scale)
    }
    this.draftBoxWasUserModified = true
    this.syncDraftBoxMatrix()
    this.boxGizmo?.update()
    this.emitChanged()
    return true
  }

  /** 清空全部已确认剪裁体并重置草稿 */
  clearVolumes(): void {
    if (this._volumes.length === 0) return
    this._volumes = []
    this.resetBoxDraft()

    if (this._active && this._selectionMode === 'box3d') {
      this.ensureBoxDraftInitialized()
      this.syncLiveBoxVolume()
    }

    this.emitChanged()
  }

  /** 清空草稿点、预览选区与 box 草稿 */
  clearDraft(): void {
    this.pendingPointer = null
    this._draftPoints = []
    this._draftPreviewPoint = null
    this.previewSelectionVolumes = []

    if (this._selectionMode === 'box3d') {
      this.resetBoxDraft()
      this.ensureBoxDraftInitialized()
      this.syncLiveBoxVolume()
    } else {
      this.resetBoxDraft()
    }

    this.emitChanged()
  }

  /**
   * 确认草稿，将预览选区提升为正式剪裁体。
   *
   * @returns 成功确认返回 true；无可确认草稿或 box3d 模式返回 false
   */
  confirmDraft(): boolean {
    if (!this.canConfirmDraft()) {
      return false
    }

    if (this._selectionMode === 'box3d') {
      return false
    }

    if (this.previewSelectionVolumes.length === 0) {
      return false
    }

    const confirmedGroupId = this.allocateConfirmedGroupId()

    for (const previewVolume of this.previewSelectionVolumes) {
      this.addConfirmedVolume({
        type: previewVolume.type,
        matrix: previewVolume.matrix.clone(),
        vertices: previewVolume.vertices ? clonePoints(previewVolume.vertices) : undefined,
        clipTask: clipModeToTask(this._clipApplyMode),
        selectionMode: previewVolume.selectionMode,
        space: previewVolume.space,
        viewMatrix: previewVolume.viewMatrix?.clone(),
        projMatrix: previewVolume.projMatrix?.clone(),
        aabb: previewVolume.aabb
          ? {
              min: previewVolume.aabb.min.clone(),
              max: previewVolume.aabb.max.clone(),
            }
          : undefined,
        groupId: confirmedGroupId,
      })
    }
    this.previewSelectionVolumes = []
    this.emitChanged()
    return true
  }

  private handleKeyDown(event: NormalizedKeyEvent): boolean {
    if (!this._active || this._selectionMode === 'box3d') {
      return false
    }

    if (!event.ctrlKey || event.key.toLowerCase() !== 'z' || !this.undoLastPoint()) {
      return false
    }

    event.domEvent.preventDefault()
    return true
  }

  private undoLastPoint(): boolean {
    if (this._draftPoints.length === 0) {
      return false
    }

    this._draftPoints.pop()
    this._draftPreviewPoint = this._draftPoints[this._draftPoints.length - 1]?.clone() ?? null
    this.emitChanged()
    return true
  }

  private handlePointerDown(event: NormalizedPointerEvent): boolean {
    if (this._selectionMode === 'box3d') {
      const consumed = this.boxGizmo?.handlePointerDown(event) ?? false
      this.syncControlLock()
      return consumed
    }

    if (event.button !== 0 && event.button !== 2) {
      return false
    }

    const allowCameraInteraction =
      this.previewSelectionVolumes.length > 0 && this._draftPoints.length === 0

    this.pendingPointer = {
      button: event.button,
      startScreen: event.screen.clone(),
      moved: false,
    }

    if (allowCameraInteraction) {
      return false
    }

    this.syncControlLock()
    return true
  }

  private handlePointerMove(event: NormalizedPointerEvent): boolean {
    if (this.pendingPointer) {
      const distance = event.screen.distanceTo(this.pendingPointer.startScreen)
      if (distance > CLICK_MOVE_THRESHOLD) {
        this.pendingPointer.moved = true
      }
    }

    if (this._selectionMode === 'box3d') {
      return this.boxGizmo?.handlePointerMove(event) ?? false
    }

    const nextPreview = toClipPoint(event.ndc)
    if (!this._draftPreviewPoint || !this._draftPreviewPoint.equals(nextPreview)) {
      this._draftPreviewPoint = nextPreview
      // Hovering before the first point must not churn overlay/clip resources.
      if (this._draftPoints.length > 0) {
        this.emitChanged()
      }
    }

    const buttons = event.domEvent?.buttons ?? 0
    if (buttons !== 0) {
      return this._draftPoints.length > 0
    }

    return true
  }

  private handlePointerUp(event: NormalizedPointerEvent): boolean {
    if (this._selectionMode === 'box3d') {
      if (event.domEvent?.type === 'pointercancel') {
        this.pendingPointer = null
      }
      const consumed = this.boxGizmo?.handlePointerUp(event) ?? false
      this.syncControlLock()
      return consumed
    }

    if (event.domEvent?.type === 'pointercancel') {
      this.pendingPointer = null
      return true
    }

    const pendingPointer = this.pendingPointer
    this.pendingPointer = null
    this.syncControlLock()

    if (!pendingPointer || pendingPointer.button !== event.button || pendingPointer.moved) {
      return this._draftPoints.length > 0
    }

    if (event.button === 2) {
      if (this._selectionMode === 'polygon' && this.canFinalizeSelection()) {
        return this.finalizeSelectionPreview()
      }

      const hadDraft = this._draftPoints.length > 0 || this._draftPreviewPoint !== null
      if (hadDraft) {
        this._draftPoints = []
        this._draftPreviewPoint = null
        this.emitChanged()
      }
      return true
    }

    if (event.button !== 0) {
      return true
    }

    const point = toClipPoint(event.ndc)

    if (this._selectionMode === 'rectangle') {
      if (this._draftPoints.length === 0) {
        this._draftPoints = [point]
        this._draftPreviewPoint = point.clone()
        this.prewarmGaussianSplatClip()
        this.emitChanged()
        return true
      }

      this._draftPoints = [this._draftPoints[0]!.clone(), point]
      this._draftPreviewPoint = point.clone()
      return this.finalizeSelectionPreview()
    }

    this._draftPoints.push(point)
    this._draftPreviewPoint = point.clone()
    this.prewarmGaussianSplatClip()
    this.emitChanged()
    return true
  }

  private canConfirmDraft(): boolean {
    switch (this._selectionMode) {
      case 'box3d':
        return false
      case 'rectangle':
      case 'polygon':
        return this.previewSelectionVolumes.length > 0
      default:
        return false
    }
  }

  private canFinalizeSelection(): boolean {
    if (this._selectionMode === 'rectangle') {
      return this._draftPoints.length === 2
    }

    if (this._selectionMode === 'polygon') {
      return this._draftPoints.length >= 3
    }

    return false
  }

  private finalizeSelectionPreview(): boolean {
    if (!this.canFinalizeSelection() || !this.currentCamera) {
      return false
    }

    const vertices =
      this._selectionMode === 'rectangle'
        ? buildRectanglePoints(this._draftPoints[0]!, this._draftPoints[1]!)
        : clonePoints(this._draftPoints)

    const volume: ClipVolume = {
      type: 'polygon',
      matrix: new THREE.Matrix4(),
      vertices,
      clipTask: ClipTask.HIGHLIGHT,
      selectionMode: this._selectionMode,
      space: 'screen',
      viewMatrix: this.currentCamera.matrixWorldInverse.clone(),
      projMatrix: this.currentCamera.projectionMatrix.clone(),
      aabb: computeClipAabb(vertices),
      groupId: PREVIEW_GROUP_ID,
    }

    this.normalizeVolume(volume)
    this.previewSelectionVolumes.push(volume)
    this._draftPoints = []
    this._draftPreviewPoint = null
    this.emitChanged()
    return true
  }

  private getPointCloudSeedBounds(): THREE.Box3 | null {
    if (!this.context.hasService('PointCloudService')) {
      return null
    }

    const pointCloudService = this.context.getService('PointCloudService') as PointCloudService
    const seedBounds = new THREE.Box3()
    let hasBounds = false

    for (const handle of pointCloudService.getAll()) {
      if (!handle.visible || !handle.object3D) {
        continue
      }

      const pointcloud = handle.object3D as THREE.Object3D & {
        getAttribute?: (name: string) => { range?: unknown } | null
        pcoGeometry?: {
          mini_tightBoundingBox?: THREE.Box3
          tightBoundingBox?: THREE.Box3
          boundingBox?: THREE.Box3 | null
        }
        updateWorldMatrix?: (updateParents: boolean, updateChildren: boolean) => void
        updateMatrixWorld?: (force?: boolean) => void
      }

      const rangeBounds = rangeToBox3(pointcloud.getAttribute?.('position')?.range)
      if (rangeBounds && !rangeBounds.isEmpty()) {
        seedBounds.union(rangeBounds)
        hasBounds = true
        continue
      }

      const localBounds =
        pointcloud.pcoGeometry?.mini_tightBoundingBox?.clone() ??
        pointcloud.pcoGeometry?.tightBoundingBox?.clone() ??
        handle.boundingBox?.clone() ??
        null

      if (!localBounds || localBounds.isEmpty()) {
        continue
      }

      if (typeof pointcloud.updateWorldMatrix === 'function') {
        pointcloud.updateWorldMatrix(true, false)
      } else if (typeof pointcloud.updateMatrixWorld === 'function') {
        pointcloud.updateMatrixWorld(true)
      }

      seedBounds.union(localBounds.applyMatrix4(pointcloud.matrixWorld))
      hasBounds = true
    }

    if (!hasBounds || seedBounds.isEmpty()) {
      return null
    }

    return seedBounds
  }

  private getBestBoxSeed(): { box: THREE.Box3; source: BoxSeedSource } | 'pending' | null {
    const pointCloudBounds = this.getPointCloudSeedBounds()
    if (pointCloudBounds && !pointCloudBounds.isEmpty()) {
      return { box: pointCloudBounds, source: 'pointcloud' }
    }

    const fitBounds = this.context.sceneBounds.getWorldBoundingBox({ purpose: 'fit' })
    if (!fitBounds.isEmpty()) {
      return { box: fitBounds, source: 'scene' }
    }

    if (this.context.hasService('PointCloudService')) {
      return 'pending'
    }

    return {
      box: new THREE.Box3(new THREE.Vector3(-0.5, -0.5, -0.5), new THREE.Vector3(0.5, 0.5, 0.5)),
      source: 'default',
    }
  }

  private getBoxSeedPriority(source: BoxSeedSource | null): number {
    switch (source) {
      case 'pointcloud':
        return 3
      case 'scene':
        return 2
      case 'default':
        return 1
      default:
        return 0
    }
  }

  private ensureBoxDraftInitialized(): void {
    const seed = this.getBestBoxSeed()
    if (!seed || seed === 'pending') {
      return
    }

    if (
      this.draftBoxMatrix &&
      (this.draftBoxWasUserModified ||
        this.getBoxSeedPriority(this.draftBoxSeedSource) >= this.getBoxSeedPriority(seed.source))
    ) {
      return
    }

    this.applyMatrixToDraftBox(composeBoxMatrix(seed.box, this.minBoxEdge))
    this.draftBoxSeedSource = seed.source
    this.draftBoxWasUserModified = false
  }

  private applyMatrixToDraftBox(matrix: THREE.Matrix4): void {
    const position = new THREE.Vector3()
    const quaternion = new THREE.Quaternion()
    const scale = new THREE.Vector3()

    matrix.decompose(position, quaternion, scale)
    scale.x = Math.max(scale.x, this.minBoxEdge)
    scale.y = Math.max(scale.y, this.minBoxEdge)
    scale.z = Math.max(scale.z, this.minBoxEdge)

    this.draftBoxAnchor.position.copy(position)
    this.draftBoxAnchor.quaternion.copy(quaternion)
    this.draftBoxAnchor.scale.copy(scale)
    this.draftBoxAnchor.updateMatrix()
    this.draftBoxAnchor.updateMatrixWorld(true)
    this.draftBoxMatrix = this.draftBoxAnchor.matrixWorld.clone()
    this.boxGizmo?.update()
  }

  private resetBoxDraft(): void {
    this.draftBoxMatrix = null
    this.draftBoxSeedSource = null
    this.draftBoxWasUserModified = false
  }

  private getLiveBoxVolume(): ClipVolume | null {
    return (
      this._volumes.find(
        (volume) => volume.groupId === LIVE_BOX_GROUP_ID && volume.type === 'box'
      ) ?? null
    )
  }

  private restoreDraftBoxFromLiveVolume(): boolean {
    const liveBox = this.getLiveBoxVolume()
    if (!liveBox) {
      return false
    }

    this.applyMatrixToDraftBox(liveBox.matrix)
    this.draftBoxSeedSource = 'pointcloud'
    this.draftBoxWasUserModified = true
    return true
  }

  private syncDraftBoxMatrix(): void {
    if (this._selectionMode !== 'box3d') return
    if (this.draftBoxMatrix === null && this.draftBoxSeedSource === null) {
      return
    }

    this.draftBoxAnchor.scale.x = Math.max(this.draftBoxAnchor.scale.x, this.minBoxEdge)
    this.draftBoxAnchor.scale.y = Math.max(this.draftBoxAnchor.scale.y, this.minBoxEdge)
    this.draftBoxAnchor.scale.z = Math.max(this.draftBoxAnchor.scale.z, this.minBoxEdge)
    this.draftBoxAnchor.updateMatrix()
    this.draftBoxAnchor.updateMatrixWorld(true)
    this.draftBoxMatrix = this.draftBoxAnchor.matrixWorld.clone()
    this.syncLiveBoxVolume()
  }

  private syncLiveBoxVolume(): void {
    if (!this._active || this._selectionMode !== 'box3d' || !this.draftBoxMatrix) {
      this.clearLiveBoxVolume()
      return
    }

    const liveVolume: ClipVolume = {
      type: 'box',
      matrix: this.draftBoxMatrix.clone(),
      clipTask: ClipTask.SHOW_INSIDE,
      groupId: LIVE_BOX_GROUP_ID,
      selectionMode: 'box3d',
    }

    this._volumes = this._volumes.filter((volume) => volume.groupId !== LIVE_BOX_GROUP_ID)
    this._volumes.push(liveVolume)
  }

  private clearLiveBoxVolume(): void {
    this._volumes = this._volumes.filter((volume) => volume.groupId !== LIVE_BOX_GROUP_ID)
  }

  private ensureBoxGizmo(): void {
    if (!this.currentCamera || this.boxGizmo || this._selectionMode !== 'box3d') {
      return
    }

    this.boxGizmo = new BoxTransformGizmo({
      minBoxEdge: this.minBoxEdge,
      onChange: () => {
        this.draftBoxWasUserModified = true
        this.syncDraftBoxMatrix()
        this.emitChanged()
      },
      onFocus: (axis) => {
        this.focusCameraOnDraftBox(axis)
      },
      onInteractingChanged: () => {
        this.syncControlLock()
      },
    })
    this.boxGizmo.attach(this.draftBoxAnchor)
    this.boxGizmo.setCamera(this.currentCamera)
  }

  private disposeBoxGizmo(): void {
    if (!this.boxGizmo) return
    this.boxGizmo.detach()
    this.boxGizmo.dispose()
    this.boxGizmo = null
  }

  private resolveActiveViewCamera(): THREE.Camera | null {
    return getActiveViewCamera(this.viewRegistry)
  }

  private resolveActiveViewId(): string | null {
    return getActiveViewId(this.viewRegistry)
  }

  private focusCameraOnDraftBox(localAxis: THREE.Vector3): void {
    this.draftBoxAnchor.updateWorldMatrix(true, false)

    const matrixWorld = this.draftBoxAnchor.matrixWorld
    const elements = matrixWorld.elements
    const center = new THREE.Vector3().setFromMatrixPosition(matrixWorld)
    const axisWorld = localAxis.clone().transformDirection(matrixWorld)
    const viewDirection = axisWorld.clone().negate()

    let up = new THREE.Vector3(0, 0, 1).transformDirection(matrixWorld)
    if (Math.abs(up.dot(viewDirection)) > 0.999) {
      up = new THREE.Vector3(0, 1, 0).transformDirection(matrixWorld)
    }
    up.addScaledVector(viewDirection, -up.dot(viewDirection)).normalize()

    const distance = this.resolveFocusDistance(elements, viewDirection, up)
    const position = center.clone()
    const worldUpAlignment = axisWorld.dot(FOCUS_WORLD_UP)
    // 轨道控制器固定使用世界 Z 作为 up，正俯/仰视需轻微避开极点以保持朝向稳定。
    if (Math.abs(worldUpAlignment) > 0.999) {
      const lateral = Math.max(distance * FOCUS_POLE_OFFSET_RATIO, EPSILON)
      const axial = Math.sqrt(Math.max(distance * distance - lateral * lateral, 0))
      position
        .addScaledVector(axisWorld, axial)
        .addScaledVector(up, -Math.sign(worldUpAlignment) * lateral)
    } else {
      position.addScaledVector(axisWorld, distance)
    }

    void this.cameraRig.animateTo(
      {
        position,
        target: center,
        up,
      },
      {
        duration: 500,
        easing: 'quarticOut',
      }
    )
  }

  private resolveFocusDistance(
    matrixElements: readonly number[],
    viewDirection: THREE.Vector3,
    up: THREE.Vector3
  ): number {
    const worldAxes = [
      new THREE.Vector3(matrixElements[0], matrixElements[1], matrixElements[2]),
      new THREE.Vector3(matrixElements[4], matrixElements[5], matrixElements[6]),
      new THREE.Vector3(matrixElements[8], matrixElements[9], matrixElements[10]),
    ]
    const maxEdge = Math.max(...worldAxes.map((axis) => axis.length()))
    const fallbackDistance = maxEdge * 2
    const camera = this.currentCamera
    const halfAxes = worldAxes.map((axis) => axis.multiplyScalar(0.5))
    const right = new THREE.Vector3().crossVectors(viewDirection, up).normalize()
    const projectedHalfExtent = (direction: THREE.Vector3): number =>
      halfAxes.reduce((sum, axis) => sum + Math.abs(axis.dot(direction)), 0)

    const halfDepth = projectedHalfExtent(viewDirection)
    const halfHeight = projectedHalfExtent(up)
    const halfWidth = projectedHalfExtent(right)

    if (camera instanceof THREE.OrthographicCamera) {
      const viewportHeight = Math.max(Math.abs(camera.top - camera.bottom), 1e-6)
      const aspect = Math.max(Math.abs(camera.right - camera.left) / viewportHeight, 1e-6)
      const tanHalfVerticalFov = Math.max(
        Math.tan(THREE.MathUtils.degToRad(this.cameraRig.fov) / 2) /
          Math.max(this.cameraRig.zoom, 1e-6),
        1e-6
      )
      const tanHalfHorizontalFov = tanHalfVerticalFov * aspect
      const projectedFit = Math.max(
        halfHeight / tanHalfVerticalFov,
        halfWidth / tanHalfHorizontalFov
      )
      const distance = Math.max(fallbackDistance, projectedFit * FOCUS_VIEW_PADDING)

      return Number.isFinite(distance) && distance > 0 ? distance : fallbackDistance
    }

    if (!(camera instanceof THREE.PerspectiveCamera)) {
      return fallbackDistance
    }

    const tanHalfVerticalFov = Math.max(
      Math.tan(THREE.MathUtils.degToRad(camera.getEffectiveFOV()) / 2),
      1e-6
    )
    const tanHalfHorizontalFov = tanHalfVerticalFov * Math.max(camera.aspect, 1e-6)
    const projectedFit = Math.max(halfHeight / tanHalfVerticalFov, halfWidth / tanHalfHorizontalFov)
    const distance = halfDepth + projectedFit * FOCUS_VIEW_PADDING

    return Number.isFinite(distance) && distance > 0 ? distance : fallbackDistance
  }

  private reattachDraftBoxAnchor(viewId: string | null): void {
    const toolsRoot = getViewToolsRoot(this.context, this.viewRegistry, viewId)
    if (this.draftBoxAnchor.parent === toolsRoot) {
      return
    }

    this.draftBoxAnchor.removeFromParent()
    toolsRoot.add(this.draftBoxAnchor)
  }

  private addConfirmedVolume(volume: ClipVolume): void {
    this.normalizeVolume(volume)
    this._volumes.push(volume)
  }

  private allocateConfirmedGroupId(): string {
    this.confirmedGroupCount += 1
    return `${CONFIRMED_GROUP_PREFIX}-${this.confirmedGroupCount}`
  }

  private syncControlLock(): void {
    if (!this.controlManager) {
      return
    }

    const shouldLock =
      this._active &&
      ((this._selectionMode !== 'box3d' && this._draftPoints.length > 0) ||
        this.boxGizmo?.isInteracting === true)
    const activeController = this.controlManager.activeController

    if (shouldLock) {
      if (activeController && this.lockedController !== activeController) {
        this.releaseControlLock()
        this.lockedController = activeController
        this.lockedControllerWasEnabled = activeController.enabled
      }

      if (this.lockedController?.enabled) {
        this.lockedController.enabled = false
      }
      return
    }

    this.releaseControlLock()
  }

  private releaseControlLock(): void {
    if (!this.lockedController) {
      return
    }

    this.lockedController.enabled = this.lockedControllerWasEnabled
    this.lockedController = null
    this.lockedControllerWasEnabled = false
  }

  private prewarmGaussianSplatClip(): void {
    if (!this.context || !this.context.hasService('GaussianSplatService')) return
    const maxVertices =
      this._selectionMode === 'box3d'
        ? 0
        : this._selectionMode === 'rectangle'
          ? 4
          : Math.max(3, this._draftPoints.length + 1)
    const service = this.context.getService('GaussianSplatService') as {
      prewarmClip?: (maxVertices: number, maxVolumes?: number) => boolean
    }
    service.prewarmClip?.(maxVertices, 1)
  }

  private releaseGaussianSplatClipPrewarm(): void {
    if (!this.context || !this.context.hasService('GaussianSplatService')) return
    const service = this.context.getService('GaussianSplatService') as {
      releaseClipPrewarm?: () => boolean
    }
    service.releaseClipPrewarm?.()
  }

  private emitChanged(): void {
    this.syncControlLock()
    const nextVolumes = this.volumes

    for (const callback of Array.from(this.listeners)) {
      try {
        callback(nextVolumes)
      } catch (error) {
        log.error(
          'ClipService.onChanged callback error: ' +
            (error instanceof Error ? error.message : String(error))
        )
      }
    }

    if (this.context?.events) {
      this.context.events.emit('tool.resultChanged', {
        name: this.name,
        data: {
          selectionMode: this._selectionMode,
          clipMode: this._clipApplyMode,
          volumeCount: nextVolumes.length,
          canConfirm: this.canConfirmDraft(),
        },
      })
    }
  }

  private normalizeVolume(volume: ClipVolume): void {
    volume.matrix = volume.matrix.clone()
    volume.vertices = volume.vertices ? clonePoints(volume.vertices) : undefined
    volume.viewMatrix = volume.viewMatrix?.clone()
    volume.projMatrix = volume.projMatrix?.clone()
    volume.aabb = volume.aabb
      ? {
          min: volume.aabb.min.clone(),
          max: volume.aabb.max.clone(),
        }
      : undefined
  }

  private validateToken(token: ActivationToken): void {
    if (token !== this.activationToken) {
      throw new Error('ClipPlugin: 非法的 ActivationToken，操作被拒绝')
    }
  }
}

export default ClipPlugin
